import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { DeleteWithReason } from '../components/DeleteWithReason';
import { useSession } from '../lib/session';
import type { CompanyRole, Grant, PersonRow, Project, ProjectStatus, WorkflowStatus } from '../lib/types';
import { ROLE_HINTS, ROLE_LABELS } from '../lib/types';
import { CATEGORY_LABELS, PriorityBadge, ProjectStatusBadge } from '../components/Badges';
import { Modal } from '../components/Modal';
import { DocumentList } from '../components/DocumentList';
import { usePaged } from '../components/ListPager';
import { ActivityCard } from './ActivityCard';
import { formatDate } from '../lib/dates';
import { DateCell } from '../components/DateCell';
import { useProjectReturn } from '../lib/projectReturn';

interface ProjectDetail {
  project: Project;
  role: CompanyRole | null;
  capabilities: Record<string, Grant>;
  statuses: WorkflowStatus[];
  /**
   * Everyone who works on this project — which is everyone with a role on its company, not
   * only the people added here. `via` says which, and `removable` follows from it.
   */
  team: Array<{
    id: string;
    name: string;
    email: string;
    role: CompanyRole;
    via: 'company' | 'project';
    removable: boolean;
  }>;
  workItemCounts: Record<string, number>;
}

export function ProjectPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { session } = useSession();
  const { backTo } = useProjectReturn(projectId);
  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [addingMember, setAddingMember] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      setDetail(await api.get<ProjectDetail>(`/projects/${projectId}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load this project');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  /* Before the early returns, as hooks must be — an empty list until the project loads. */
  const teamPage = usePaged(detail?.team ?? [], 'projectTeam');

  if (error) return <div className="callout">{error}</div>;
  if (!detail) return <div className="empty">Loading…</div>;

  const { project, statuses, team, capabilities, workItemCounts } = detail;
  const canEdit = capabilities['project.manage'] === 'full';
  const total = Object.values(workItemCounts).reduce((sum, n) => sum + n, 0);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow crumbs">
            {/*
              Whoever came in through Project management goes back to the project they were
              on, not to a list of all of them — and not to the company, which is a door they
              never used.
            */}
            {backTo && (
              <>
                <Link to={backTo} className="muted">
                  Project management
                </Link>
                <span aria-hidden>›</span>
              </>
            )}
            <Link to={`/companies/${project.companyId}`} className="muted">
              {backTo ? 'Company' : 'Back to company'}
            </Link>
          </div>
          <div className="row wrap" style={{ gap: 12 }}>
            <h1>{project.name}</h1>
            <ProjectStatusBadge status={project.status} />
            <PriorityBadge priority={project.priority} />
            {project.restricted && <span className="badge badge-accent">restricted</span>}
            <span className={project.sharedWithClient ? 'badge badge-info' : 'badge'}>
              {project.sharedWithClient ? 'shared with client' : 'ours only'}
            </span>
            {!project.billable && <span className="badge">non-billable</span>}
          </div>
          <p className="subtitle">
            <span className="key">{project.code}</span>
            {project.description ? ` · ${project.description}` : ''}
          </p>
        </div>
        <div className="row">
          {/* Stays inside Project management when that is where the board is being read. */}
          <Link to={backTo ?? `/projects/${project.id}/board`} className="btn btn-primary">
            Open board
          </Link>
          {canEdit && (
            <button type="button" className="btn" onClick={() => setEditing(true)}>
              Edit project
            </button>
          )}
        </div>
      </div>

      <div className="stat-row mb-2">
        <div className="stat">
          <div className="stat-label">Work items</div>
          <div className="stat-value">{total}</div>
        </div>
        <div className="stat">
          <div className="stat-label">In progress</div>
          <div className="stat-value">{workItemCounts.active ?? 0}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Blocked</div>
          <div className="stat-value" style={{ color: workItemCounts.blocked ? 'var(--accent)' : undefined }}>
            {workItemCounts.blocked ?? 0}
          </div>
        </div>
        <div className="stat">
          <div className="stat-label">Target date</div>
          <div className="stat-value" style={{ fontSize: 18, color: project.overdue ? 'var(--accent)' : undefined }}>
            {formatDate(project.targetDate)}
          </div>
        </div>
        <div className="stat">
          <div className="stat-label">Team</div>
          <div className="stat-value">{team.length}</div>
        </div>
      </div>

      {/* Money: only there for whoever may see it — the API leaves `budget` off otherwise. */}
      {'budget' in project && (
        <BudgetCard
          projectId={project.id}
          budget={project.budget ?? null}
          mayEdit={capabilities['company.manage'] === 'full'}
          onEdit={() => setEditing(true)}
        />
      )}

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}>
        <div className="card">
          <div className="card-head">
            <h2>Workflow</h2>
            <span className="dim" style={{ fontSize: 12 }}>
              {statuses.length} columns
            </span>
          </div>
          <div className="card-pad stack-sm">
            {statuses.map((status) => (
              <div key={status.id} className="row-between" style={{ padding: '5px 0' }}>
                <div className="row" style={{ gap: 9 }}>
                  <span className="dot" style={{ background: status.color }} />
                  <span style={{ fontWeight: 600 }}>{status.label}</span>
                  <span className="badge">{CATEGORY_LABELS[status.category]}</span>
                </div>
                <span className="dim mono" style={{ fontSize: 11 }}>
                  {status.gitlabLabel || '—'}
                </span>
              </div>
            ))}
            <p className="dim" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
              The right column shows the GitLab label each state maps to, so an issue moved in
              GitLab lands in the matching column here.
            </p>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Team</h2>
            {canEdit && (
              <button type="button" className="btn btn-sm" onClick={() => setAddingMember(true)}>
                {project.restricted ? 'Add member' : 'Set a different role'}
              </button>
            )}
          </div>

          {/*
            SAYS WHAT THE RULES ACTUALLY ARE, which the old copy did not.

            It read "Nobody is explicitly on this project" beside an Add member button, so
            everybody assumed a person had to be added here before they could work — and added
            them twice. A role on the company already is a place on its projects; this list
            exists to OVERRIDE that for one project, or to be the whole team on a restricted
            one.
          */}
          <div className="card-pad" style={{ paddingBottom: 0 }}>
            <p className="dim" style={{ margin: 0, fontSize: 12 }}>
              {project.restricted
                ? 'This project is restricted, so only the people listed here can see it at all — a role on the company is not enough.'
                : 'Everyone with a role on this company works on this project; nobody needs adding. A row here only gives somebody a different role for this project.'}
            </p>
          </div>

          {team.length === 0 ? (
            <div className="empty">
              <p style={{ margin: 0 }}>
                {project.restricted
                  ? 'Nobody has been added, so nobody can see this project.'
                  : 'Nobody has a role on this company yet.'}
              </p>
            </div>
          ) : (
            <>
            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>How</th>
                    {canEdit && <th />}
                  </tr>
                </thead>
                <tbody>
                  {teamPage.visible.map((member) => (
                    <tr key={member.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{member.name}</div>
                        <div className="dim" style={{ fontSize: 12 }}>
                          {member.email}
                        </div>
                      </td>
                      <td>
                        <span className="badge">{ROLE_LABELS[member.role]}</span>
                      </td>
                      <td>
                        {/* Where the role came from, so "why can they see this" has an answer. */}
                        {member.via === 'company' ? (
                          <span className="dim" style={{ fontSize: 12 }}>
                            Via the company
                          </span>
                        ) : (
                          <span className="badge badge-info">Set here</span>
                        )}
                      </td>
                      {canEdit && (
                        <td className="right">
                          {/* Only an override can be taken off here; company access is removed on the company. */}
                          {member.removable && (
                            <RemoveMemberButton projectId={project.id} personId={member.id} onDone={load} />
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {teamPage.pager && <div className="card-pad" style={{ paddingTop: 8 }}>{teamPage.pager}</div>}
            </>
          )}
        </div>
      </div>

      <div className="card mt-2">
        <div className="card-head">
          <h2>Repository</h2>
        </div>
        <div className="card-pad">
          {project.gitlabProjectPath ? (
            <div className="stack-sm">
              <div className="row-between">
                <span className="muted">GitLab project</span>
                <span className="mono">{project.gitlabProjectPath}</span>
              </div>
              <div className="row-between">
                <span className="muted">Default branch</span>
                <span className="mono">{project.gitlabDefaultBranch || 'not set'}</span>
              </div>
              <p className="dim" style={{ fontSize: 12, marginBottom: 0 }}>
                Merge requests, approvals and the merge itself land here in the changes phase.
              </p>
            </div>
          ) : (
            <p className="dim" style={{ margin: 0 }}>
              No repository linked yet. Add the GitLab project path to bring merge requests into the Hub.
            </p>
          )}
        </div>
      </div>

      {editing && (
        <EditProjectModal
          project={project}
          mayBudget={capabilities['company.manage'] === 'full'}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void load();
          }}
        />
      )}

      {/*
        The signed change requests and the handover, against the project as a whole.
        SECOND, not last: a thing people use should not be pushed down the page. The agreed
        plan, releases and risks cards that once sat below it were retired; a document
        uploaded here does that job now.
      */}
      <div className="mt-2">
        <DocumentList scope="projects" id={project.id} canUpload={capabilities['work.write'] !== 'none'} />
      </div>

      {/*
        What has been happening here, last on the page — it is history, and the things you come
        to a project to DO belong above it. The API decides how much of it each audience gets;
        the card adds nothing of its own.
      */}
      <div className="mt-2">
        <ActivityCard projectId={project.id} />
      </div>

      {/*
        Administrators only, below the history — deleting a project takes the board and
        everything it is, so it sits at the very bottom rather than beside the controls people
        use to run it.
      */}
      {session?.person.isAdmin && (
        <div className="mt-2">
          <DeleteWithReason
            what="project"
            name={project.name}
            code={project.code}
            path={`/projects/${project.id}`}
            onDeleted={() => navigate(`/companies/${project.companyId}`, { replace: true })}
          />
        </div>
      )}

      {addingMember && (
        <AddMemberModal
          projectId={project.id}
          existing={team.map((t) => t.id)}
          onClose={() => setAddingMember(false)}
          onAdded={() => {
            setAddingMember(false);
            void load();
          }}
        />
      )}
    </>
  );
}

function RemoveMemberButton({ projectId, personId, onDone }: { projectId: string; personId: string; onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.del(`/projects/${projectId}/team/${personId}`);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" className="btn btn-quiet btn-sm" onClick={remove} disabled={busy}>
        Remove
      </button>
      {error && <div className="field-error">{error}</div>}
    </>
  );
}

function EditProjectModal({
  project,
  mayBudget,
  onClose,
  onSaved,
}: {
  project: Project;
  /** Whether this person may set money on the project — the rate card's own grant. */
  mayBudget: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(project.name);
  const [budget, setBudget] = useState(project.budget == null ? '' : String(project.budget));
  const [description, setDescription] = useState(project.description);
  const [status, setStatus] = useState<ProjectStatus>(project.status);
  const [priority, setPriority] = useState(project.priority);
  const [targetDate, setTargetDate] = useState(project.targetDate ?? '');
  const [gitlabProjectPath, setGitlabProjectPath] = useState(project.gitlabProjectPath);
  const [gitlabDefaultBranch, setGitlabDefaultBranch] = useState(project.gitlabDefaultBranch);
  const [sharedWithClient, setSharedWithClient] = useState(project.sharedWithClient);
  const [restricted, setRestricted] = useState(project.restricted);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const movingTarget = !!project.baselineTargetDate && targetDate !== project.baselineTargetDate;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}`, {
        name,
        description,
        status,
        priority,
        targetDate: targetDate || undefined,
        gitlabProjectPath,
        gitlabDefaultBranch,
        sharedWithClient,
        restricted,
        ...(mayBudget ? { budget: budget.trim() === '' ? null : Number(budget.replace(/[\s,R]/g, '')) } : {}),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the project');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Edit project"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="edit-project" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      {error && <div className="callout">{error}</div>}
      <form id="edit-project" onSubmit={submit} noValidate>
        <label className="field">
          <span className="field-label">Project name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
        </label>

        <label className="field">
          <span className="field-label">Description</span>
          <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
          <label className="field grow">
            <span className="field-label">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value as ProjectStatus)}>
              <option value="discovery">Discovery</option>
              <option value="active">Active</option>
              <option value="on_hold">On hold</option>
              <option value="delivered">Delivered</option>
              <option value="archived">Archived</option>
            </select>
          </label>

          <label className="field grow">
            <span className="field-label">Priority</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value as typeof priority)}>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="none">None</option>
            </select>
          </label>
        </div>

        <div className="field">
          <span className="field-label">Target date</span>
          <DateCell
            value={targetDate || null}
            editable
            emptyLabel="Pick a date"
            onChange={(next) => setTargetDate(next ?? '')}
          />
          {movingTarget && (
            <span className="field-hint" style={{ color: 'var(--warn)' }}>
              The baseline stays at {formatDate(project.baselineTargetDate)}, so this shows as slippage. That is deliberate.
            </span>
          )}
        </div>

        {mayBudget && (
          <label className="field">
            <span className="field-label">Budget (rand)</span>
            <input
              type="text"
              inputMode="decimal"
              value={budget}
              placeholder="No budget set"
              onChange={(event) => setBudget(event.target.value)}
            />
            <span className="field-hint">
              What was agreed for this project. Spent and planned cost are compared with it on the project and the company
              overview. Leave empty for no budget.
            </span>
          </label>
        )}

        <label className="field">
          <span className="field-label">GitLab project path</span>
          <input type="text" className="mono" value={gitlabProjectPath} onChange={(e) => setGitlabProjectPath(e.target.value)} />
        </label>

        <label className="field">
          <span className="field-label">Default branch</span>
          <input type="text" className="mono" value={gitlabDefaultBranch} onChange={(e) => setGitlabDefaultBranch(e.target.value)} />
        </label>

        <div className="checkbox-row">
          <input
            id="edit-shared"
            type="checkbox"
            checked={sharedWithClient}
            onChange={(e) => setSharedWithClient(e.target.checked)}
          />
          <label htmlFor="edit-shared">
            <strong>Share with the client</strong>
            <div className="dim" style={{ fontSize: 12 }}>
              {sharedWithClient
                ? "The customer's people can see and work on this."
                : 'Ours only — the customer cannot see that this project exists.'}
            </div>
          </label>
        </div>

        <div className="checkbox-row">
          <input id="edit-restricted" type="checkbox" checked={restricted} onChange={(e) => setRestricted(e.target.checked)} />
          <label htmlFor="edit-restricted">
            <strong>Restricted to the project team</strong>
            <div className="dim" style={{ fontSize: 12 }}>
              Even our own staff need to be on the team to open it.
            </div>
          </label>
        </div>

        {/*
          SHOWN, NOT OFFERED.

          Whether a project charges for its time is settled when it is created, because every
          hour booked here inherits it — changing it later would leave a project whose own
          entries disagree with it, first month billable and second not, with nothing on any row
          saying why.

          It is stated here rather than left off the form, which is what it was: this is where
          somebody looks for it, and a setting that is nowhere on the page it belongs to reads
          as a setting that does not exist.
        */}
        <div className="field" style={{ marginBottom: 0 }}>
          <span className="field-label">Billing</span>
          <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
            <span className={project.billable ? 'badge' : 'badge badge-warn'}>
              {project.billable ? 'Billable' : 'Non-billable'}
            </span>
            <span className="dim" style={{ fontSize: 12 }}>
              {project.billable
                ? 'Hours booked here are chargeable unless somebody says otherwise on the entry.'
                : 'Hours booked here are not charged.'}
            </span>
          </div>
          <span className="field-hint">
            Set when the project was created and fixed for good — hours already booked were
            charged on that basis. A project set up the wrong way round needs a new project.
          </span>
        </div>
      </form>
    </Modal>
  );
}

function AddMemberModal({
  projectId,
  existing,
  onClose,
  onAdded,
}: {
  projectId: string;
  existing: string[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [people, setPeople] = useState<PersonRow[] | null>(null);
  const [personId, setPersonId] = useState('');
  const [role, setRole] = useState<CompanyRole>('user');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<PersonRow[]>('/people')
      .then((rows) => setPeople(rows.filter((p) => p.isActive && !existing.includes(p.id))))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load people'));
  }, [existing]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/team`, { personId, role });
      onAdded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add that person');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Add team member"
      subtitle="They must already be a member of this company."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="add-member" className="btn btn-primary" disabled={busy || !personId}>
            {busy ? 'Adding…' : 'Add to project'}
          </button>
        </>
      }
    >
      {error && <div className="callout">{error}</div>}
      <form id="add-member" onSubmit={submit}>
        <label className="field">
          <span className="field-label">Person</span>
          <select value={personId} onChange={(e) => setPersonId(e.target.value)} required>
            <option value="">Select a person…</option>
            {(people ?? []).map((person) => (
              <option key={person.id} value={person.id}>
                {person.name} — {person.email}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field-label">Role on this project</span>
          <select value={role} onChange={(e) => setRole(e.target.value as CompanyRole)}>
            {(Object.keys(ROLE_LABELS) as CompanyRole[]).map((key) => (
              <option key={key} value={key}>
                {ROLE_LABELS[key]}
              </option>
            ))}
          </select>
          <span className="field-hint">{ROLE_HINTS[role]} Overrides their company role for this project only.</span>
        </label>
      </form>
    </Modal>
  );
}

const rand = (value: number) => `R ${Math.round(value).toLocaleString('en-ZA').replace(/\u00a0/g, ' ')}`;

/**
 * The project's budget against what is spent and what is planned.
 *
 * Spent is the hours booked at the company's rate card; planned is every task's estimate at
 * the same rates — both read off the plan, so this agrees with the Gantt's cost columns and
 * the company overview. Unknown, not zero, when there is no rate to price them at.
 */
function BudgetCard({
  projectId,
  budget,
  mayEdit,
  onEdit,
}: {
  projectId: string;
  budget: number | null;
  mayEdit: boolean;
  onEdit: () => void;
}) {
  const [costs, setCosts] = useState<{ spent: number | null; planned: number | null } | null>(null);

  useEffect(() => {
    let live = true;
    // Money arrives as a string ("20800.00"), so it is read through Number before adding up.
    type Row = { has_children?: boolean; actual_cost?: number | string | null; planned_cost?: number | string | null };
    api
      .get<{ rows: Row[]; unscheduled: Row[] }>(`/projects/${projectId}/timeline?include_closed=true`)
      .then((plan) => {
        if (!live) return;
        const leaves = [...plan.rows, ...plan.unscheduled].filter((row) => !row.has_children);
        const total = (pick: (row: Row) => number | string | null | undefined) => {
          const values = leaves
            .map((row) => {
              const v = pick(row);
              const n = typeof v === 'string' ? Number(v) : v;
              return typeof n === 'number' && Number.isFinite(n) ? n : null;
            })
            .filter((v): v is number => v !== null);
          return values.length ? values.reduce((a, b) => a + b, 0) : null;
        };
        setCosts({ spent: total((row) => row.actual_cost), planned: total((row) => row.planned_cost) });
      })
      .catch(() => live && setCosts({ spent: null, planned: null }));
    return () => {
      live = false;
    };
  }, [projectId, budget]);

  const spent = costs?.spent ?? null;
  const planned = costs?.planned ?? null;
  const over = budget !== null && spent !== null && spent > budget;
  const plannedOver = budget !== null && planned !== null && planned > budget;
  const pct = (value: number | null) => (budget && value !== null ? Math.min(100, (value / budget) * 100) : 0);

  return (
    <div className="card mb-2">
      <div className="card-head">
        <h2>Budget</h2>
        {mayEdit && (
          <button type="button" className="btn btn-quiet btn-sm" onClick={onEdit}>
            {budget === null ? 'Set a budget' : 'Change'}
          </button>
        )}
      </div>
      <div className="card-pad">
        {budget === null ? (
          <p className="dim" style={{ margin: 0 }}>
            No budget set{spent !== null ? ` — ${rand(spent)} spent so far` : ''}
            {planned !== null ? `, ${rand(planned)} planned` : ''}.
          </p>
        ) : (
          <div className="stack-sm">
            <div className="dash-line">
              <span className="dash-line-name">Spent</span>
              <span className="mono" style={over ? { color: 'var(--dash-bad)', fontWeight: 700 } : undefined}>
                {spent === null ? 'Not known' : rand(spent)} of {rand(budget)}
              </span>
              <div className="dash-bar">
                <span className={`dash-bar-fill ${over ? 'tone-bad' : 'tone-info'}`} style={{ width: `${pct(spent)}%` }} />
              </div>
            </div>
            <div className="dash-line">
              <span className="dash-line-name">Planned</span>
              <span className="mono" style={plannedOver ? { color: 'var(--dash-bad)', fontWeight: 700 } : undefined}>
                {planned === null ? 'Not known' : rand(planned)} of {rand(budget)}
              </span>
              <div className="dash-bar">
                <span className={`dash-bar-fill ${plannedOver ? 'tone-bad' : 'tone-plan'}`} style={{ width: `${pct(planned)}%` }} />
              </div>
            </div>
            <p className="dim dash-note" style={over || plannedOver ? { color: 'var(--dash-bad)' } : undefined}>
              {over
                ? `Over budget by ${rand(spent! - budget)}.`
                : plannedOver
                  ? `The plan costs ${rand(planned! - budget)} more than the budget.`
                  : spent === null && planned === null
                    ? 'Spent and planned cost need a rate card on the company to be worked out.'
                    : `${rand(budget - (spent ?? 0))} left.`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
